<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed, watch } from 'vue';

import CartSummary from '@/components/Cart/CartSummary/CartSummary.vue';
import GiftCollectionDisplay from '@/components/Cart/FreeGift/GiftCollectionDisplay.vue';
import ThresholdVisualizer from '@/components/Cart/ThresholdsBar/ThresholdVisualizer.vue';
import CrossSellBlock from '@/components/CrossSellBlock/CrossSellBlock.vue';
import IconX from '@/shared/components/UI/Icons/IconX.vue';
import SideDrawer from '@/shared/components/UI/SideDrawer.vue';
import { useTranslations } from '@/shared/composables/useTranslations';
import { useCartStore } from '@/shared/pinia/modules/cart';

import MiniCartEmptyState from './MiniCartEmptyState.vue';
import MiniCartFooter from './MiniCartFooter/MiniCartFooter.vue';
import MiniCartLines from './MiniCartLines/MiniCartLines.vue';

type Props = {
  shippingThreshold: number;
  lowestShippingPrice: number;
  showSalesTaxMessage: boolean;
  giftThreshold: number;
  scenario: 'none' | 'shipping_only' | 'dual_threshold';
  initialShowThreshold: number;
  giftCollectionHandle: string;
  crossSellCollectionHandle: string;
};

defineProps<Props>();

const cartStore = useCartStore();
const { loadingCart, miniCartOpen } = storeToRefs(cartStore);
const { t } = useTranslations();

const itemCount = computed(() => cartStore.cart?.item_count ?? 0);
const isCartEmpty = computed(() => itemCount.value === 0);

// Syncs Trustpilot widget visibility with the mini cart open state and lazily
// initializes the widget on first open (Trustpilot requires an explicit load call).
let trustpilotLoaded = false;
const handleTrustpilot = (open: boolean) => {
  document
    .querySelector('.js-mini-cart-trustpilot-static')
    ?.classList.toggle('is-open', open);

  if (open && !trustpilotLoaded) {
    trustpilotLoaded = true;
    setTimeout(() => {
      const widget = document.querySelector<HTMLElement>(
        '.js-mini-cart-trustpilot-static .trustpilot-widget'
      );
      if (window.Trustpilot && widget?.dataset.templateId) {
        window.Trustpilot.loadFromElement(widget, true);
      }
    }, 100);
  }
};

watch(miniCartOpen, handleTrustpilot);
</script>

<template>
  <SideDrawer
    v-model:active="cartStore.miniCartOpen"
    bare
    panel-class="z-dropdown-container flex h-dvh w-full flex-col overflow-hidden border shadow-lg md:max-w-[650px]"
  >
    <template #default="{ close }">
      <div
        :class="{ 'is-loading': loadingCart }"
        class="flex flex-1 flex-col pb-10 text-gray-darkest"
      >
        <!-- Header -->
        <div
          id="mini-cart-title"
          class="flex items-center justify-between gap-2 border-b bg-white py-3 pl-4 pr-3 text-sm font-bold md:pl-8 md:text-base"
        >
          <h2 class="flex items-center text-xl">
            {{ t('cart.your_cart') }} ({{ itemCount }})
          </h2>
          <button
            class="z-20 cursor-pointer rounded-full bg-gray-100 p-2 text-gray-600 transition-colors hover:bg-gray-200 hover:text-gray-800"
            :aria-label="t('global.accessibility.close')"
            @click="close"
          >
            <IconX class="size-6" />
          </button>
        </div>

        <!-- Content -->
        <MiniCartEmptyState v-if="isCartEmpty" />

        <template v-else>
          <!-- iOS Safari won't scroll a flex-grow child whose height it can't measure.
               The relative/flex-1 wrapper gives the area a bounded height; the
               absolute/inset-0 inner div fills it exactly so iOS has concrete
               dimensions for the scroll container. @touchmove.stop prevents
               tua-body-scroll-lock's document listener from calling preventDefault
               on touches inside the cart, which was blocking native scroll entirely.
               .passive marks the listener as passive so iOS can still run its
               optimized scroll-axis detection - without it, child horizontal
               scrollers (cross-sell slider, gift carousel) get hijacked by the
               parent's vertical scroll because the non-passive listener forces
               Safari to wait on JS before deciding direction. -->
          <div class="relative flex-1">
            <div
              class="absolute inset-0 overflow-y-auto"
              style="-webkit-overflow-scrolling: touch"
              @touchmove.stop.passive
            >
              <ThresholdVisualizer
                v-if="scenario !== 'none'"
                :shipping-threshold="shippingThreshold"
                :gift-threshold="giftThreshold"
                :gift-collection-handle="giftCollectionHandle"
                :display-divider="true"
                :scenario="scenario"
                :initial-show-threshold="initialShowThreshold"
                design="rounded"
              />

              <GiftCollectionDisplay
                v-if="scenario === 'dual_threshold'"
                :collection-handle="giftCollectionHandle"
                :gift-threshold="giftThreshold"
                :shipping-threshold="shippingThreshold"
                :max-products="3"
              />

              <MiniCartLines />
              <CartSummary
                :shipping-threshold="shippingThreshold ?? 0"
                :lowest-shipping-price="lowestShippingPrice ?? 0"
                display="mini"
              />

              <!-- CE-306: feature disabled in code as well as via PostHog flag
                   (show_cart_cross_sell at 0%). Hardcoded kill is intentional -
                   removing this v-if requires a deliberate code change.
                   See top-of-file JSDoc in CrossSellBlock.vue. -->
              <CrossSellBlock
                v-if="false"
                :collection-handle="crossSellCollectionHandle"
              />
            </div>
          </div>

          <MiniCartFooter
            :shipping-threshold="shippingThreshold"
            :lowest-shipping-price="lowestShippingPrice"
            :show-sales-tax-message="showSalesTaxMessage"
          />
        </template>
      </div>
    </template>
  </SideDrawer>
</template>
